import { HttpResponse } from '@angular/common/http';
import { Component, ɵIS_HYDRATION_DOM_REUSE_ENABLED } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ProjectService } from 'src/app/Services/project.service';
import { SharedService } from 'src/app/Services/sharedService';
import swal from 'sweetalert2';
import { config } from 'src/app/config';
import * as XLSX from 'xlsx';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectionService } from 'src/app/Services/device-detection.service';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';

@Component({
    selector: 'app-import-users',
    templateUrl: './import-users.component.html',
    styleUrl: './import-users.component.css',
    standalone: false
})
export class ImportUsersComponent {

  uploadCSVForm: UntypedFormGroup;
  fileHeaders = ['first_name', 'last_name', 'email', 'password', 'role']
  disabled: boolean = true;
  csvDisabled: boolean = false;
  headerError: boolean = false;
  downloadCsv: string = config.import_employee_sample;
  errorMessageForValidate: any = {
    noDataFound: "No employee data was found in the uploaded file. Please ensure the file contains data and follows the required format.",
    invalidFileType: "The uploaded file is not in CSV format. Please ensure you upload a CSV file."
  }
  headerErrorMsg: any = {
    message: "",
    hasError: false
  };
  noDataErrorMsg: any = {
    message: "",
    hasError: false
  };
  fileObjectToShow: any = {
    fileName: "",
    hasShowObject: false
  }

  constructor(
    private _projectService: ProjectService,
    private _sharedService: SharedService,
    private dialog: MatDialog,
    private deviceDetectionService: DeviceDetectionService,
    private phoneVerificationService: PhoneVerificationService
  ) {
  }

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.uploadCSVForm = new UntypedFormGroup({
      csv: new UntypedFormControl(''),
      sendEmail: new UntypedFormControl(false)
    })
  }

  uploadCSV(event: any): void {
    const file = event?.srcElement?.files?.[0] || event;

    if (!file) {
      console.error("No file found in event.");
      return;
    }

    this.uploadCSVForm.get('csv').setValue(file);

    this.headerErrorMsg = {
      message: "",
      hasError: false
    };
    this.noDataErrorMsg = {
      message: "",
      hasError: false
    };

    const fileName = file.name;
    if (!fileName.endsWith('.csv')) {
      this.headerErrorMsg = {
        message: this.errorMessageForValidate?.invalidFileType,
        hasError: true
      };
      this.fileObjectToShow = {
        fileName: file.name,
        hasShowObject: true,
      };
      return;
    }

    // Parse CSV content from the first worksheet.
    const fileReader = new FileReader();
    fileReader.onload = () => {
      try {
        const buffer = fileReader.result as ArrayBuffer;
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames?.[0];
        const firstSheet = firstSheetName ? workbook.Sheets[firstSheetName] : null;
        const parsedData = firstSheet
          ? (XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            blankrows: false,
            raw: false
          }) as any[][])
          : [];

        const headers = (parsedData[0] || []).map((header) => String(header).trim());
        let data = parsedData.slice(1).filter((row) => Array.isArray(row));
        data = data.filter((row) => row.length >= this.fileHeaders.length);

        // Check for missing headers
        const missingHeaders = new Set(this.fileHeaders);
        headers.forEach((header) => {
          const title = header.trim();
          if (missingHeaders.has(title)) {
            missingHeaders.delete(title);
          }
        });

        if (missingHeaders.size > 0) {
          let errorMsg = "The uploaded file is missing the required columns: ";
          errorMsg += Array.from(missingHeaders).join(", ");
          this.disabled = true;
          this.headerErrorMsg = {
            message: errorMsg,
            hasError: true
          };
          this.fileObjectToShow = {
            fileName: file.name,
            hasShowObject: true,
            data: data.length
          };
        } else if (data.length === 0) {
          this.disabled = true;
          this.noDataErrorMsg = {
            message: this.errorMessageForValidate.noDataFound,
            hasError: true
          };
          this.fileObjectToShow = {
            fileName: file.name,
            hasShowObject: true,
            data: data.length
          };
        } else {
          this.disabled = false;
          this.fileObjectToShow = {
            fileName: file.name,
            hasShowObject: true,
            data: data.length
          };
        }
      } catch (err) {
        console.error("Error parsing CSV:", err);
        this.headerErrorMsg = {
          message: "An error occurred while processing the file.",
          hasError: true
        };
      }
    };
    fileReader.readAsArrayBuffer(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault(); // Prevent default behavior (open as link for files)
    const dropZone = event.target as HTMLElement;
    dropZone.classList.add('drag-over'); // Optional: Add a CSS class to change appearance
  }

  onDragLeave(event: DragEvent) {
    const dropZone = event.target as HTMLElement;
    dropZone.classList.remove('drag-over'); // Optional: Remove the CSS class
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      const file = files[0]; // Get the first file
      this.uploadCSV(file); // Call the existing upload logic
    }
  }

  modalClose() {
    if (this._sharedService.modalinstance) {
      this._sharedService.modalinstance.close();
    }
    // this.uploadCSVForm.get('csv').setValue('');
    this.uploadCSVForm.reset()
    this.disabled = true;
    this.csvDisabled = false;
    this.headerErrorMsg = {
      message: "",
      hasError: false
    };
    this.noDataErrorMsg = {
      message: "",
      hasError: false
    };
    this.fileObjectToShow = {
      fileName: "",
      hasShowObject: false
    }

  }

  importEmployees() {
    const performImport = () => {
      this.disabled = true;
      this.csvDisabled = true;
      this.uploadCSVForm.disable();
      this._projectService.uploadCSVFile(this.uploadCSVForm.value).subscribe((res: HttpResponse<any>) => {
        const contentDisposition = res?.headers?.get('content-disposition');
        const filename = contentDisposition?.match(/filename="(.+?)"/)?.[1] || 'ss-created-employees.csv';

        const resBody = res?.body;
        this.uploadCSVForm.enable();
        this.uploadCSVForm.reset();
        this.disabled = false;
        this.csvDisabled = false;
        const blob = new Blob([resBody?.csv_data], { type: 'text/csv' });
        const successfullImports = resBody?.success;
        const failedImports = resBody?.failed;


        var downloadURL = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = downloadURL;
        link.download = filename;
        link.click();

        this._projectService.updateEmployeeList({})
        swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Employees Imported!',
          html: `
          <p style="margin-top: 0px;">Check employee status in the downloaded report.</p>
          <b>
            <span style="color: #21bc68">
              Success:  
            </span>
            <span style="margin-right: 20px">
              ${successfullImports}
            </span>
            <span style="color: #E84646">
              Failed: 
            </span>
            <span>
              ${failedImports}
            </span>
          </b>`,
          showConfirmButton: false,
          timer: 5000,
        }).then(() => {
          this.modalClose();
        })
      }, (error: any) => {
        this.disabled = false;
        this.csvDisabled = false;
        this.uploadCSVForm.enable();
        swal.fire({
          icon: 'error',
          title: 'Not Imported!',
          text: 'Employees Not Imported. Try Again!',
          timer: 3000,
        })
      });
    };

    // If desktop or already verified, proceed with import
    performImport();
  }

}
